import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

const platformsWithABlackStatusBar = ['android', 'ios'];

/**
 * The icon style to ask the OS status bar for on a native platform.
 *
 * The plugin names a style after the background it is meant for: Style.Dark
 * gives light icons (for a dark bar) and Style.Light gives dark icons.
 *
 * MUS-578: on Android the bar sits on black on every OS version (the status bar
 * colour up to Android 14, the window background behind the transparent bar
 * from Android 15), so dark icons were close to unreadable there.
 *
 * MUS-606: on iOS the bar sits on black too. Once the app asks the plugin not
 * to overlay the webview (below), the plugin covers the status bar area with a
 * view of its own, black unless a colour is set, and the app sets none. Dark
 * icons could not be seen on it.
 */
export const statusBarStyleFor = (platform: string): Style =>
  platformsWithABlackStatusBar.includes(platform) ? Style.Dark : Style.Light;

/**
 * Keep the OS status bar visible, lay the webview out below it where the OS
 * still allows that, and ask for readable icons.
 *
 * MUS-577: Android 15+ ignores the overlay request (edge to edge is enforced);
 * there the native shell keeps the webview clear of the bars instead, see
 * android.adjustMarginsForEdgeToEdge in capacitor.config.ts.
 */
export async function configureNativeStatusBar(): Promise<void> {
  await StatusBar.setOverlaysWebView({ overlay: false });
  await StatusBar.setStyle({ style: statusBarStyleFor(Capacitor.getPlatform()) });
}
